import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Button, Modal, Form, Row, Col, Badge, Spinner } from 'react-bootstrap';
import { Plus, Pencil, Trash2, DoorOpen, Image as ImageIcon, X, ListCheck } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { useBranch } from '../../context/BranchContext';
import { roomAPI, cleanupActionAPI } from '../../services/room';
import { branchAPI } from '../../services/branch';
import { cloudinaryService } from '../../services/cloudinary';

// Catalog tiện ích (code khớp với AMENITY_CATALOG bên menu app)
const AMENITY_OPTIONS = [
  { code: 'AC', label: 'Máy lạnh' },
  { code: 'FAN', label: 'Quạt' },
  { code: 'TV', label: 'Tivi' },
  { code: 'PS5', label: 'PlayStation' },
  { code: 'PROJECTOR', label: 'Máy chiếu' },
  { code: 'SOFA', label: 'Sofa' },
  { code: 'WIFI', label: 'Wifi' },
  { code: 'KARAOKE', label: 'Karaoke' },
];

const amenityLabel = (code) =>
  AMENITY_OPTIONS.find((a) => a.code === code)?.label || code;

const tenChuan = (s) => (s || '').trim();

/* ───────────────── Room Modal ───────────────── */
const RoomModal = ({ show, onHide, room, branchId, actions, bookable, onSave }) => {
  const emptyForm = {
    name: '',
    description: '',
    capacity: 4,
    hourlyPrice: 0,
    minBookingGuests: '',
    sortOrder: 0,
    isActive: true,
    amenities: [],
    imageUrls: [],
    defaultCleanupActions: [],
    bookable: true,
  };
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [customAmenity, setCustomAmenity] = useState('');
  const fileRef = useRef(null);

  useEffect(() => {
    if (room) {
      setForm({
        name: room.name || '',
        description: room.description || '',
        capacity: room.capacity ?? 4,
        hourlyPrice: room.hourly_price ?? 0,
        minBookingGuests:
          room.min_booking_guests == null ? '' : room.min_booking_guests,
        sortOrder: room.sort_order ?? 0,
        isActive: room.is_active ?? true,
        amenities: room.amenities || [],
        imageUrls: room.image_urls || [],
        defaultCleanupActions: room.default_cleanup_actions || [],
        bookable,
      });
    } else {
      setForm({ ...emptyForm, bookable: true });
    }
    setCustomAmenity('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, show, bookable]);

  // Tiện ích custom = đã chọn nhưng không thuộc catalog
  const customAmenities = form.amenities.filter(
    (c) => !AMENITY_OPTIONS.some((o) => o.code === c),
  );

  const addCustomAmenity = () => {
    const val = customAmenity.trim();
    if (!val) return;
    if (!form.amenities.includes(val)) {
      setForm((prev) => ({ ...prev, amenities: [...prev.amenities, val] }));
    }
    setCustomAmenity('');
  };

  const removeAmenity = (code) =>
    setForm((prev) => ({
      ...prev,
      amenities: prev.amenities.filter((c) => c !== code),
    }));

  const toggleIn = (key, value) =>
    setForm((prev) => {
      const has = prev[key].includes(value);
      return {
        ...prev,
        [key]: has ? prev[key].filter((v) => v !== value) : [...prev[key], value],
      };
    });

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    try {
      setUploading(true);
      const results = await Promise.all(
        files.map((f) => cloudinaryService.uploadImage(f, { folder: 'rooms' })),
      );
      const urls = results.map((r) => r?.secure_url).filter(Boolean);
      setForm((prev) => ({ ...prev, imageUrls: [...prev.imageUrls, ...urls] }));
      toast.success('Upload ảnh thành công');
    } catch (err) {
      toast.error(err?.message || 'Upload ảnh thất bại');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeImage = (idx) =>
    setForm((prev) => ({
      ...prev,
      imageUrls: prev.imageUrls.filter((_, i) => i !== idx),
    }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Vui lòng nhập tên phòng');
      return;
    }
    // #182340 — giữ nguyên 2 phép kiểm cũ của khu "Phòng riêng" (nay đã gom vào đây):
    // sức chứa ≥ 1 và số khách tối thiểu không được lớn hơn sức chứa.
    const sucChua = Number(form.capacity);
    if (!Number.isInteger(sucChua) || sucChua < 1) {
      toast.error('Sức chứa phải là số nguyên ≥ 1');
      return;
    }
    if (form.minBookingGuests !== '' && form.minBookingGuests !== null) {
      const min = Number(form.minBookingGuests);
      if (!Number.isInteger(min) || min < 1) {
        toast.error('Số khách tối thiểu phải là số nguyên ≥ 1');
        return;
      }
      if (min > sucChua) {
        toast.error('Số khách tối thiểu không được lớn hơn sức chứa');
        return;
      }
    }
    onSave({ ...form, branchId });
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{room ? 'Chỉnh sửa phòng' : 'Thêm phòng mới'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Tên phòng *</Form.Label>
                <Form.Control
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="VD: Phòng lớn, Cinema Couple, PlayStation..."
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Sức chứa</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={form.capacity}
                  onChange={(e) => setForm({ ...form, capacity: Number(e.target.value) })}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Giá thuê / giờ (VND)</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  step={1000}
                  value={form.hourlyPrice}
                  onChange={(e) =>
                    setForm({ ...form, hourlyPrice: Number(e.target.value) })
                  }
                  placeholder="VD: 40000 (0 = miễn phí)"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Số khách tối thiểu để đặt</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={form.minBookingGuests}
                  onChange={(e) =>
                    setForm({ ...form, minBookingGuests: e.target.value })
                  }
                  placeholder="Để trống = không giới hạn"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Mô tả</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Mô tả trải nghiệm phòng (hiển thị cho khách)"
            />
          </Form.Group>

          {/* Amenities */}
          <Form.Group className="mb-3">
            <Form.Label>Tiện ích (hiển thị cho khách)</Form.Label>
            <div className="d-flex flex-wrap gap-3">
              {AMENITY_OPTIONS.map((a) => (
                <Form.Check
                  key={a.code}
                  type="checkbox"
                  id={`amenity-${a.code}`}
                  label={a.label}
                  checked={form.amenities.includes(a.code)}
                  onChange={() => toggleIn('amenities', a.code)}
                />
              ))}
            </div>

            {/* Tiện ích custom đã thêm */}
            {customAmenities.length > 0 && (
              <div className="d-flex flex-wrap gap-2 mt-2">
                {customAmenities.map((code) => (
                  <Badge
                    key={code}
                    bg="info"
                    className="fw-normal d-flex align-items-center gap-1 py-2 px-2"
                  >
                    {code}
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-white lh-1"
                      onClick={() => removeAmenity(code)}
                    >
                      <X size={13} />
                    </Button>
                  </Badge>
                ))}
              </div>
            )}

            {/* Thêm tiện ích tự do */}
            <div className="d-flex gap-2 mt-2" style={{ maxWidth: 360 }}>
              <Form.Control
                size="sm"
                placeholder="Thêm tiện ích khác (VD: Bàn bi-a)"
                value={customAmenity}
                onChange={(e) => setCustomAmenity(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCustomAmenity();
                  }
                }}
              />
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={addCustomAmenity}
              >
                <Plus size={15} />
              </Button>
            </div>
          </Form.Group>

          {/* Default cleanup actions */}
          <Form.Group className="mb-3">
            <Form.Label>Hành động dọn mặc định (cho bàn trong phòng)</Form.Label>
            {actions.length === 0 ? (
              <div className="text-muted small">
                Chưa có hành động dọn. Thêm ở mục "Hành động dọn" phía trên.
              </div>
            ) : (
              <div className="d-flex flex-wrap gap-3">
                {actions.map((a) => (
                  <Form.Check
                    key={a.id}
                    type="checkbox"
                    id={`def-action-${a.id}`}
                    label={a.label}
                    checked={form.defaultCleanupActions.includes(a.code)}
                    onChange={() => toggleIn('defaultCleanupActions', a.code)}
                  />
                ))}
              </div>
            )}
          </Form.Group>

          {/* Images */}
          <Form.Group className="mb-3">
            <Form.Label>Hình ảnh</Form.Label>
            <div className="d-flex flex-wrap gap-2 mb-2">
              {form.imageUrls.map((url, i) => (
                <div key={i} className="position-relative">
                  <img
                    src={url}
                    alt={`room-${i}`}
                    style={{ width: 96, height: 72, objectFit: 'cover', borderRadius: 6 }}
                  />
                  <Button
                    variant="danger"
                    size="sm"
                    className="position-absolute top-0 end-0 p-0 d-flex align-items-center justify-content-center"
                    style={{ width: 20, height: 20, transform: 'translate(30%,-30%)', borderRadius: '50%' }}
                    onClick={() => removeImage(i)}
                  >
                    <X size={12} />
                  </Button>
                </div>
              ))}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="d-none"
              onChange={handleUpload}
            />
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
            >
              {uploading ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <ImageIcon size={15} className="me-1" />
              )}
              {uploading ? 'Đang upload…' : 'Thêm ảnh'}
            </Button>
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Thứ tự hiển thị</Form.Label>
                <Form.Control
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
                />
              </Form.Group>
            </Col>
            <Col md={6} className="d-flex align-items-end">
              <Form.Check
                type="switch"
                id="room-active"
                label="Đang hoạt động"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
            </Col>
          </Row>

          <Form.Group className="mt-3">
            <Form.Check
              type="switch"
              id="room-bookable"
              label="Cho khách đặt phòng này trên app"
              checked={form.bookable}
              onChange={(e) => setForm({ ...form, bookable: e.target.checked })}
            />
            <Form.Text className="text-muted">
              Bật thì phòng hiện trong luồng khách đặt bàn trên app (dùng tên, sức chứa, giá/giờ và
              số khách tối thiểu ở ngay form này). Tắt &quot;Đang hoạt động&quot; cũng đồng thời
              ngưng cho khách đặt phòng này.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Hủy
          </Button>
          <Button variant="primary" type="submit" disabled={uploading}>
            {room ? 'Cập nhật' : 'Thêm'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

/* ───────────────── Cleanup Action catalog ───────────────── */
const ActionCatalog = ({ branchId, actions, onChanged }) => {
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const addAction = async () => {
    if (!code.trim() || !label.trim()) {
      toast.error('Nhập đủ mã và nhãn hành động');
      return;
    }
    try {
      setSaving(true);
      await cleanupActionAPI.createAction({
        branchId,
        code: code.trim().toUpperCase(),
        label: label.trim(),
        sortOrder: actions.length + 1,
      });
      setCode('');
      setLabel('');
      toast.success('Đã thêm hành động');
      onChanged();
    } catch (err) {
      toast.error(err?.message || 'Không thêm được hành động');
    } finally {
      setSaving(false);
    }
  };

  const removeAction = async (id) => {
    if (!window.confirm('Xóa hành động này?')) return;
    try {
      await cleanupActionAPI.deleteAction(id);
      toast.success('Đã xóa');
      onChanged();
    } catch (err) {
      toast.error(err?.message || 'Không xóa được');
    }
  };

  return (
    <Card className="shadow-sm mb-4">
      <Card.Header className="bg-light d-flex align-items-center gap-2">
        <ListCheck size={18} />
        <strong>Hành động dọn (catalog)</strong>
        <span className="text-muted small">— dùng để gán theo phòng / override theo bàn</span>
      </Card.Header>
      <Card.Body>
        <div className="d-flex flex-wrap gap-2 mb-3">
          {actions.length === 0 && (
            <span className="text-muted small">Chưa có hành động nào.</span>
          )}
          {actions.map((a) => (
            <Badge
              key={a.id}
              bg="light"
              text="dark"
              className="border d-flex align-items-center gap-2 py-2 px-3"
            >
              <span className="fw-semibold">{a.label}</span>
              <code className="small text-muted">{a.code}</code>
              <Button
                variant="link"
                size="sm"
                className="p-0 text-danger"
                onClick={() => removeAction(a.id)}
              >
                <Trash2 size={13} />
              </Button>
            </Badge>
          ))}
        </div>
        <Row className="g-2 align-items-end">
          <Col xs={4} md={3}>
            <Form.Label className="small mb-1">Mã</Form.Label>
            <Form.Control
              size="sm"
              placeholder="AC_OFF"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </Col>
          <Col xs={5} md={4}>
            <Form.Label className="small mb-1">Nhãn</Form.Label>
            <Form.Control
              size="sm"
              placeholder="Tắt máy lạnh"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </Col>
          <Col xs={3} md={2}>
            <Button size="sm" onClick={addAction} disabled={saving}>
              <Plus size={15} /> Thêm
            </Button>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

/* ───────────────── Main ───────────────── */
const AdminRooms = ({ hideTitle = false }) => {
  // #175019 — chi nhánh chỉ chọn ở thanh trên đầu (`BranchContext`), màn này không còn ô riêng.
  const { selectedBranch } = useBranch();
  const branchId = selectedBranch?.id || '';
  const [rooms, setRooms] = useState([]);
  const [actions, setActions] = useState([]);
  // #182340 — tên các phòng ĐANG cho khách đặt trên app (`Branch.private_rooms`)
  const [tenChoDat, setTenChoDat] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async (bid) => {
    if (!bid) return [];
    try {
      setLoading(true);
      const [roomRes, actionRes, branchRes] = await Promise.all([
        roomAPI.getRooms({ branchId: bid }),
        cleanupActionAPI.getActions({ branchId: bid }),
        branchAPI.getBranchById(bid),
      ]);
      const dsPhong = roomRes?.data || [];
      const dsKhachDat = branchRes?.data?.private_rooms || branchRes?.private_rooms || [];
      setRooms(dsPhong);
      setActions(actionRes?.data || []);
      setTenChoDat(new Set(dsKhachDat.map((r) => tenChuan(r.name))));
      return dsPhong;
    } catch (err) {
      toast.error(err?.message || 'Không tải được dữ liệu phòng');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (branchId) load(branchId);
  }, [branchId, load]);

  const handleSave = async (data) => {
    const tenMoi = tenChuan(data.name);
    // Luồng khách đặt bàn khớp phòng theo TÊN ⇒ không cho 2 phòng cùng tên trong một chi nhánh.
    const trungTen = rooms.some(
      (r) => r.id !== editing?.id && tenChuan(r.name) === tenMoi,
    );
    if (trungTen) {
      toast.error(`Chi nhánh đã có phòng tên "${tenMoi}"`);
      return;
    }
    try {
      // #154555 — `bookable` đi KÈM trong chính request lưu phòng. Máy chủ ghi bảng `rooms` và
      // `Branch.private_rooms` trong CÙNG một giao dịch, nên không còn cửa sổ lệch dữ liệu khi
      // một trong hai bước hỏng (trước đây là 2 request tách rời, bước 2 hỏng chỉ hiện toast).
      if (editing) {
        await roomAPI.updateRoom(editing.id, data);
        toast.success('Cập nhật phòng thành công');
      } else {
        await roomAPI.createRoom(data);
        toast.success('Tạo phòng thành công');
      }
      setShowModal(false);
      setEditing(null);
      await load(branchId);
    } catch (err) {
      toast.error(err?.message || 'Lưu phòng thất bại');
    }
  };

  const handleDelete = async (room) => {
    if (!window.confirm('Xóa phòng này? Bàn trong phòng sẽ chuyển về khu chung.')) return;
    try {
      // #154555 — máy chủ tự gỡ phòng khỏi `Branch.private_rooms` trong cùng giao dịch xoá.
      await roomAPI.deleteRoom(room.id);
      toast.success('Đã xóa phòng');
      await load(branchId);
    } catch (err) {
      toast.error(err?.message || 'Không xóa được phòng');
    }
  };

  return (
    <div className="mb-4">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3 mb-4">
        <div>
          {!hideTitle && (
            <>
              <h4 className="mb-1">Quản lý Phòng</h4>
              <small className="text-muted">Phòng, tiện ích, ảnh & hành động dọn theo chi nhánh</small>
            </>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            size="sm"
            disabled={!branchId}
            onClick={() => {
              setEditing(null);
              setShowModal(true);
            }}
          >
            <Plus size={18} className="me-1" /> Thêm phòng
          </Button>
        </div>
      </div>

      {!branchId && (
        <div className="alert alert-warning">
          Chọn chi nhánh ở thanh trên đầu trang để xem và quản lý phòng.
        </div>
      )}

      {branchId && (
        <ActionCatalog
          branchId={branchId}
          actions={actions}
          onChanged={() => load(branchId)}
        />
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <Row className="g-3">
          {rooms.map((room) => (
            <Col key={room.id} xs={12} sm={6} lg={4}>
              <Card className="h-100 border">
                {room.image_urls?.[0] && (
                  <Card.Img
                    variant="top"
                    src={room.image_urls[0]}
                    style={{ height: 140, objectFit: 'cover' }}
                  />
                )}
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="d-flex align-items-center flex-wrap gap-2">
                      <DoorOpen size={18} className="text-primary" />
                      <span className="fw-bold">{room.name}</span>
                      {!room.is_active && <Badge bg="secondary">Ẩn</Badge>}
                      {room.is_active && !tenChoDat.has(tenChuan(room.name)) && (
                        <Badge bg="light" text="dark" className="border fw-normal">
                          Khách không đặt được
                        </Badge>
                      )}
                    </div>
                    <div className="d-flex gap-1">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          setEditing(room);
                          setShowModal(true);
                        }}
                      >
                        <Pencil size={15} />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(room)}
                      >
                        <Trash2 size={15} />
                      </Button>
                    </div>
                  </div>
                  {room.description && (
                    <p className="text-muted small mb-2">{room.description}</p>
                  )}
                  <div className="d-flex flex-wrap gap-1 mb-2">
                    {(room.amenities || []).map((code) => (
                      <Badge key={code} bg="info" className="fw-normal">
                        {amenityLabel(code)}
                      </Badge>
                    ))}
                  </div>
                  {(room.default_cleanup_actions || []).length > 0 && (
                    <div className="small text-muted">
                      Dọn: {(room.default_cleanup_actions || [])
                        .map((c) => actions.find((a) => a.code === c)?.label || c)
                        .join(', ')}
                    </div>
                  )}
                  <div className="small text-muted mt-1">
                    Sức chứa: {room.capacity}
                    {room.hourly_price > 0 &&
                      ` · ${room.hourly_price.toLocaleString('vi-VN')}đ/h`}
                    {room.min_booking_guests != null &&
                      ` · tối thiểu ${room.min_booking_guests} khách`}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
          {branchId && rooms.length === 0 && (
            <Col xs={12}>
              <Card className="shadow-sm">
                <Card.Body className="text-center py-5 text-muted">
                  Chưa có phòng nào cho chi nhánh này.
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>
      )}

      <RoomModal
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setEditing(null);
        }}
        room={editing}
        branchId={branchId}
        actions={actions}
        bookable={editing ? tenChoDat.has(tenChuan(editing.name)) : true}
        onSave={handleSave}
      />
    </div>
  );
};

export default AdminRooms;
